import { DefectDataItem } from "../types/DefectsDataItem";
import { DefectStatus } from "../types/DefectStatus";

export const MAX_PSC_RISKS = 10;

/** The dashboard is a summary; the Defects tab shows MAX_PSC_RISKS. */
export const MAX_DASHBOARD_PSC_RISKS = 5;

/** Matches the neighbouring charts. */
export const MAX_LABEL_NAME_LENGTH = 40;

/**
 * Declared structurally so this stays a pure function with no dependency on
 * the client that fetches the data. ResolvedRegimeMappings satisfies it.
 * Keyed by Idwal defect code.
 */
export type RegimeMappingsForRisk = Record<
    string,
    {
        regime: string;
        code: string;
        name?: string;
        category?: string;
        subCategory?: string;
    }[]
>;

/**
 * Both attributes are merged onto the defect client side by
 * mergeGradesWithDefects - neither is on the record as it arrives.
 */
export type PscRiskGroupBy = "vesselName" | "technicalManager";

/** A single vessel's / technical manager's share of one bar. */
export interface PscRiskGroup {
    name: string;
    count: number;
    /** The click through filters the table on these, as the whole bar does. */
    idwalCodes: string[];
}

/** Stands in for a defect whose vessel or technical manager is blank. */
export const DEFAULT_UNKNOWN_GROUP = "Unknown";

/**
 * Matches the Most Common Defects breakdown. Cycled rather than indexed, since
 * a fleet can have more vessels than there are colours.
 */
const groupColours = ["#B4CC47", "#E62788", "#FFA504", "#3495D2"];

/** Presentation agnostic - the Plotly mapping happens below. */
export interface PscRiskIndicator {
    /** MoU, PMoU or TMoU - shown as the code's prefix. */
    regime: string;
    code: string;
    name?: string;
    category?: string;
    subCategory?: string;
    count: number;
    /** For the click through: the defects table filters on Idwal codes. */
    idwalCodes: string[];
    label: string;
    /**
     * Highest count first, and empty without a groupBy - which is how the
     * chart decides whether to stack.
     */
    groups: PscRiskGroup[];
}

const riskKey = (regime: string, code: string) => regime + ":" + code;

const truncateName = (name: string) =>
    name.length > MAX_LABEL_NAME_LENGTH
        ? name.substring(0, MAX_LABEL_NAME_LENGTH) + "..."
        : name;

/**
 * The design shows two aligned columns, but Plotly tick labels cannot align
 * columns in a proportional font, so the code leads and the name follows.
 */
export const buildPscRiskLabel = (
    regime: string,
    code: string,
    name?: string
): string => {
    const prefixed = `${regime}: ${code}`;

    return name ? `${prefixed}  ${truncateName(name)}` : prefixed;
};

/**
 * Counts how often each regime code is raised by the defects in view.
 *
 * Only expired defects are excluded - deliberately no date cutoff, unlike Most
 * Common Defects, so the two charts will not agree on totals. Catch-all codes
 * such as "Other (machinery)" are kept, though they may lead the chart. "High
 * only" and the Paris/Tokyo collapse are applied upstream by the prep script
 * and cannot be filtered here.
 *
 * Bars are keyed on regime and code. On today's workbook a code only ever
 * carries one regime, so that is equivalent to keying on the code alone.
 *
 * One defect contributes to every code it maps to, so the bars sum to more
 * than the number of defects.
 *
 * Capped here rather than by slicing the trace, so customdata cannot fall out
 * of step with the bars.
 *
 * A groupBy splits each bar per vessel / technical manager. The split is
 * tallied inside the same dedupe as the total, so the groups always sum back
 * to count.
 */
export const buildPscRiskIndicators = (
    defects: DefectDataItem[],
    mappings: RegimeMappingsForRisk,
    maxItems: number = MAX_PSC_RISKS,
    groupBy?: PscRiskGroupBy,
    unknownGroup: string = DEFAULT_UNKNOWN_GROUP
): PscRiskIndicator[] => {
    const risks = new Map<string, PscRiskIndicator>();
    const groups = new Map<string, Map<string, PscRiskGroup>>();

    defects.forEach((defect) => {
        const typeCode = defect.typeCode;

        if (defect.status === DefectStatus.expired || !typeCode) {
            return;
        }

        const mapped = mappings[typeCode];

        if (!mapped?.length) {
            return;
        }

        // A defect counts once per code even if the mapping data lists it
        // twice - it should not, but it must not double count if it does.
        const counted = new Set<string>();

        mapped.forEach((mapping) => {
            const key = riskKey(mapping.regime, mapping.code);

            if (counted.has(key)) {
                return;
            }

            counted.add(key);

            if (groupBy) {
                const groupName = defect[groupBy]?.trim() || unknownGroup;
                let byName = groups.get(key);

                if (!byName) {
                    byName = new Map<string, PscRiskGroup>();
                    groups.set(key, byName);
                }

                const group = byName.get(groupName);

                if (group) {
                    group.count += 1;

                    if (!group.idwalCodes.includes(typeCode)) {
                        group.idwalCodes.push(typeCode);
                    }
                } else {
                    byName.set(groupName, {
                        name: groupName,
                        count: 1,
                        idwalCodes: [typeCode]
                    });
                }
            }

            const existing = risks.get(key);

            if (existing) {
                existing.count += 1;

                if (!existing.idwalCodes.includes(typeCode)) {
                    existing.idwalCodes.push(typeCode);
                }

                return;
            }

            risks.set(key, {
                regime: mapping.regime,
                code: mapping.code,
                name: mapping.name,
                category: mapping.category,
                subCategory: mapping.subCategory,
                count: 1,
                idwalCodes: [typeCode],
                label: buildPscRiskLabel(
                    mapping.regime,
                    mapping.code,
                    mapping.name
                ),
                groups: []
            });
        });
    });

    return Array.from(risks.entries())
        .sort(([, a], [, b]) => b.count - a.count)
        .slice(0, maxItems)
        .map(([key, risk]) => ({
            ...risk,
            groups: Array.from(groups.get(key)?.values() ?? []).sort(
                (a, b) => b.count - a.count || a.name.localeCompare(b.name)
            )
        }));
};

const pscRiskTraceDefaults = {
    type: "bar" as const,
    orientation: "h" as const,
    // Without these the count sits at the end of the bar, not centred in it,
    // unlike defectPlotDefaults and mostCommonPlotDefaults.
    textfont: {
        color: "white",
        size: 14
    },
    textposition: "inside" as const,
    insidetextanchor: "middle" as const
};

/** Bold code, then the ellipsed name - the y tick and the category key. */
const pscRiskAxisLabel = (risk: PscRiskIndicator) =>
    `<b>${risk.regime}: ${risk.code}</b>  ` +
    `${risk.name ? truncateName(risk.name) : ""}`;

/**
 * groupName fills Plotly's secondary hover box, which is what names the vessel
 * or technical manager a stacked segment belongs to.
 */
const pscRiskHoverTemplate = (risk: PscRiskIndicator, groupName?: string) =>
    `<b>${risk.name ?? risk.code}</b><br>` +
    `Code: ${risk.regime}: ${risk.code}<br>` +
    `Sub category: ${risk.subCategory ?? "-"}<br>` +
    `—<br>Mappings: %{x}<extra>${groupName ?? ""}</extra>`;

/**
 * The y categories in count order. Stacked traces each carry only the bars
 * they contribute to, so without this the axis orders itself off whichever
 * trace happens to come first.
 */
export const pscRiskCategoryOrder = (risks: PscRiskIndicator[]) =>
    risks.map(pscRiskAxisLabel);

/**
 * customdata carries the Idwal defect codes because the defects table filters
 * on those, and because the ellipsed y label cannot identify a bar.
 */
export const mapPscRiskIndicators = (risks: PscRiskIndicator[]) => ({
    ...pscRiskTraceDefaults,
    x: risks.map((risk) => risk.count),
    y: risks.map(pscRiskAxisLabel),
    customdata: risks.map((risk) => risk.idwalCodes),
    marker: { color: "#28235B" },
    text: risks.map((risk) => String(risk.count)),
    hovertemplate: risks.map((risk) => pscRiskHoverTemplate(risk))
});

/**
 * One stacked trace per vessel / technical manager. Each holds only the bars
 * its group contributes to, so the axis needs pscRiskCategoryOrder to keep
 * them in count order.
 *
 * trace.name carries the group, which is what the click through reads to add
 * the vessel or technical manager to the table filters.
 */
export const mapGroupedPscRiskIndicators = (risks: PscRiskIndicator[]) => {
    const totals = new Map<string, number>();

    risks.forEach((risk) =>
        risk.groups.forEach((group) =>
            totals.set(group.name, (totals.get(group.name) ?? 0) + group.count)
        )
    );

    // Biggest contributor first, so the legend and the stacking agree.
    const names = Array.from(totals.entries())
        .sort(([nameA, a], [nameB, b]) => b - a || nameA.localeCompare(nameB))
        .map(([name]) => name);

    return names.map((name, index) => {
        const contributions = risks
            .map((risk) => ({
                risk,
                group: risk.groups.find((group) => group.name === name)
            }))
            .filter(
                (entry): entry is { risk: PscRiskIndicator; group: PscRiskGroup } =>
                    entry.group !== undefined
            );

        return {
            ...pscRiskTraceDefaults,
            name,
            x: contributions.map((entry) => entry.group.count),
            y: contributions.map((entry) => pscRiskAxisLabel(entry.risk)),
            customdata: contributions.map((entry) => entry.group.idwalCodes),
            marker: { color: groupColours[index % groupColours.length] },
            text: contributions.map((entry) => String(entry.group.count)),
            hovertemplate: contributions.map((entry) =>
                pscRiskHoverTemplate(entry.risk, name)
            )
        };
    });
};

/** Derived once on load rather than per table row. */
export const buildRegimeDescriptions = (
    mappings: RegimeMappingsForRisk
): Record<string, string> => {
    const descriptions: Record<string, string> = {};

    Object.keys(mappings).forEach((idwalCode) => {
        mappings[idwalCode].forEach((mapping) => {
            if (mapping.name) {
                descriptions[riskKey(mapping.regime, mapping.code)] =
                    mapping.name;
            }
        });
    });

    return descriptions;
};

/** The shape DefectCodeChips renders as pills. */
export const buildAuthorityMappings = (
    mappings: RegimeMappingsForRisk
): Record<string, string[]> => {
    const authority: Record<string, string[]> = {};

    Object.keys(mappings).forEach((idwalCode) => {
        authority[idwalCode] = mappings[idwalCode].map((mapping) =>
            riskKey(mapping.regime, mapping.code)
        );
    });

    return authority;
};
